/**
 * AI-powered group name suggestion using LiteLLM Claude Haiku.
 * Ported from deleted Next.js route: /api/groups/suggest-name
 */

import {chatCompletion, LITELLM_MODELS} from '@/lib/litellm-client';
import {logger} from '@/lib/logger';

const log = logger.tag('SuggestName');

const SYSTEM_PROMPT = `You are a professional furniture curator creating concise, descriptive names for furniture collections.

Your task is to analyze a list of furniture products and create a short, descriptive group name that clearly identifies what the collection contains.

Guidelines:
- Keep it SHORT: 2-5 words maximum
- Focus on WHAT the products are, not their style
- Be highly descriptive and specific
- Include main product names when relevant (e.g., table, chair, cabinet names)
- Use clear furniture terminology (e.g., "Dining Ensemble", "Bedroom Set", "Seating Collection")
- Ignore style descriptors like "Modern", "Coastal", "Industrial" - focus on function and product types
- Don't use quotation marks in the name

Examples of good names:
- Madison Dining Ensemble
- Harper Bedroom Collection
- Conference Table & Seating
- Chelsea Sofa & Chairs
- Aspen Cabinet Set
- Dining Table & Chairs`;

interface ProductInfo {
    name: string;
    sku?: string;
    brand?: string;
}

interface SuggestNameResult {
    success: boolean;
    suggestedName?: string;
    error?: string;
}

export async function suggestGroupName(
    products: ProductInfo[],
    currentName?: string
): Promise<SuggestNameResult> {
    try {
        const productList = products
            .map((p, index) => {
                const parts = [p.name];
                if (p.brand && p.brand.toLowerCase() !== 'floorplanner') {
                    parts.push(`by ${p.brand}`);
                }
                if (p.sku) {
                    parts.push(`(${p.sku})`);
                }
                return `${index + 1}. ${parts.join(' ')}`;
            })
            .join('\n');

        const userPrompt = `Based on these furniture products, suggest a concise, descriptive name for this group that clearly identifies what products it contains:

${productList}

${currentName ? `Current name: "${currentName}"\n` : ''}
Focus on the product types and names (e.g., if it's dining furniture, use "Dining Ensemble" or include table/chair names). Be specific and descriptive, not stylistic.

Return ONLY the suggested group name, nothing else. No quotation marks, no explanations.`;

        const response = await chatCompletion({
            model: LITELLM_MODELS.haiku,
            messages: [
                {
                    role: 'user',
                    content: SYSTEM_PROMPT + '\n\n' + userPrompt,
                },
            ],
            max_tokens: 50,
        });

        const suggestedName = response.choices[0]?.message?.content
            ?.trim()
            .replace(/^["']|["']$/g, '');

        if (!suggestedName) {
            return {success: false, error: 'No content in response'};
        }

        return {success: true, suggestedName};
    } catch (error) {
        log.error('Failed to suggest group name', error);
        const message =
            error instanceof Error ? error.message : 'Unknown error';
        return {success: false, error: message};
    }
}
